import React, { useEffect, useRef, useState, useCallback } from 'react';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import { Writer } from 'moddle-xml';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import { useTheme } from '../../hooks/useTheme';
import ToolbarBpmn from './ToolbarBpmn';
import BpmnCodePanel from './BpmnCodePanel';
import { getDiagramById, updateDiagram, createDiagram, publishDiagram,
         resolveConflictAcceptGitlab, resolveConflictAcceptPlatform } from '../../api/diagramsApi';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { xmlToDsl } from '../../utils/bpmnDslParser';
import DiagramPublishModal from '../DiagramPublishModal';
import DiagramToastModal from '../DiagramToastModal';
import DiagramConflictModal from '../DiagramConflictModal';
import DiagramHistoryModal from '../DiagramHistoryModal';
import DiagramModal from '../DiagramModal';
import { collectPublishLintDiagnostics } from '../../utils/publishLintDiagnostics';
import {
    DIAGRAM_DRAFT_STATUS,
    DIAGRAM_EXIT_CONFIRM_MESSAGE,
    buildDiagramDraftKey,
    clearDiagramDraft,
    readDiagramDraft,
    withDraftStatus,
    writeDiagramDraft,
} from '../../utils/diagramDrafts';

const CODE_PANEL_WIDTH = 640;

const DEFAULT_BPMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_1" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="173" y="102" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const BoardBpmn = ({ diagramId = null, projectId = null }) => {
    const { theme } = useTheme();
    const containerRef = useRef(null);
    const modelerRef = useRef(null);

    // State
    const [currentDiagramId, setCurrentDiagramId] = useState(diagramId);
    const [currentDiagramMeta, setCurrentDiagramMeta] = useState(null);
    const [diagramGitlabPath, setDiagramGitlabPath] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [isPublishing, setIsPublishing] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishLintDiagnostics, setPublishLintDiagnostics] = useState([]);
    const [showDiagramModal, setShowDiagramModal] = useState(false);
    const [showCodePanel, setShowCodePanel] = useState(false);
    const [toast, setToast] = useState(null); // { type: 'success'|'error', title, message? }
    const [showConflict, setShowConflict] = useState(false);
    const [isResolvingConflict, setIsResolvingConflict] = useState(false);
    const currentDiagramIdRef = useRef(currentDiagramId);
    currentDiagramIdRef.current = currentDiagramId;
    const dslElementMapRef = useRef(new Map());
    const [codePanelDsl, setCodePanelDsl] = useState(diagramId ? '' : undefined);
    // Tracks the latest DSL text typed in the code panel (may differ from codePanelDsl which only
    // updates when Sync to Code is clicked). Used to persist DSL on save.
    const latestDslRef = useRef('');
    const dslSourceRef = useRef('loaded');
    const latestBpmnXmlRef = useRef(DEFAULT_BPMN_XML);
    const draftDirtyRef = useRef(false);
    const draftRevisionRef = useRef(0);
    const diagramGenerationRef = useRef(0);
    const imperativeLoadRef = useRef(null);
    const modalSaveContextRef = useRef(null);
    const draftKeyRef = useRef(null);
    const persistLocalDraftRef = useRef(null);
    const desiredImportsRef = useRef(new WeakMap());
    const importRenderDepthRef = useRef(new WeakMap());
    const importIdentityRef = useRef(0);
    const newDiagramLoadGenerationRef = useRef(0);

    // Lane context menu
    const [laneCtxMenu, setLaneCtxMenu] = useState(null);
    // { x, y, systemCode, laneName }

    const markDraftChanged = useCallback(() => {
        draftRevisionRef.current += 1;
        draftDirtyRef.current = true;
    }, []);

    const importXmlIntoModeler = useCallback(async (xml, isCurrent = () => true, desiredImport = null) => {
        const modeler = modelerRef.current;
        if (!modeler || !isCurrent() || modeler.destroyed) return false;
        const desired = desiredImport || { identity: ++importIdentityRef.current, xml, pending: true };
        if (!desiredImport) {
            desiredImportsRef.current.set(modeler, desired);
        } else {
            desired.pending = true;
        }
        const isCurrentImport = () => (
            isCurrent()
            && modelerRef.current === modeler
            && !modeler.destroyed
            && desiredImportsRef.current.get(modeler) === desired
        );
        try {
            await modeler.importXML(xml);
            if (!isCurrentImport()) {
                const currentDesired = desiredImportsRef.current.get(modeler);
                if (currentDesired && currentDesired !== desired && modelerRef.current === modeler && !modeler.destroyed) {
                    await importXmlIntoModeler(currentDesired.xml, () => (
                        modelerRef.current === modeler
                        && !modeler.destroyed
                        && desiredImportsRef.current.get(modeler) === currentDesired
                    ), currentDesired);
                }
                return false;
            }
            latestBpmnXmlRef.current = xml;
            return true;
        } finally {
            if (desiredImportsRef.current.get(modeler) === desired) {
                desired.pending = false;
            }
        }
    }, []);

    // Initialize Modeler
    useEffect(() => {
        if (!containerRef.current) return;
        const container = containerRef.current;

        const modeler = new BpmnModeler({
            container,
        });

        modelerRef.current = modeler;

        modeler.on('canvas.viewbox.changed', (e) => {
            setZoom(e.viewbox.scale);
        });

        const handleImportRenderStart = () => {
            const depths = importRenderDepthRef.current;
            depths.set(modeler, (depths.get(modeler) || 0) + 1);
        };

        const handleImportRenderComplete = () => {
            const depths = importRenderDepthRef.current;
            depths.set(modeler, Math.max(0, (depths.get(modeler) || 1) - 1));
        };

        const handleImportDone = (event) => {
            if (event?.error) {
                handleImportRenderComplete();
            }
        };

        modeler.on('import.render.start', handleImportRenderStart);
        modeler.on('import.render.complete', handleImportRenderComplete);
        modeler.on('import.done', handleImportDone);

        const handleCommandStackChanged = () => {
            if ((importRenderDepthRef.current.get(modeler) || 0) > 0) return;

            try {
                const xml = new Writer({ format: true }).toXML(modeler.getDefinitions());
                latestBpmnXmlRef.current = xml;
                desiredImportsRef.current.set(modeler, {
                    identity: ++importIdentityRef.current,
                    xml,
                    pending: false,
                });
                dslSourceRef.current = 'visual';
                markDraftChanged();
                persistLocalDraftRef.current?.();
            } catch (error) {
                console.warn('[BPMNDraft] visual XML snapshot failed:', error);
            }
        };
        modeler.on('commandStack.changed', handleCommandStackChanged);

        // ── Right-click context menu for lanes with systemCode ──
        const handleContextMenu = (e) => {
            e.preventDefault();

            const elementRegistry = modeler.get('elementRegistry');
            if (!elementRegistry) return;

            // Find the element at the click position
            // bpmn.io renders into an SVG; walk up from the event target
            // to find a data-element-id attribute
            let target = e.target;
            let bpmnElementId = null;
            while (target && target !== containerRef.current) {
                const id = target.getAttribute('data-element-id');
                if (id) { bpmnElementId = id; break; }
                target = target.parentElement;
            }

            if (!bpmnElementId) return;

            const shape = elementRegistry.get(bpmnElementId);
            if (!shape) return;

            // Check if it's a lane with a systemCode extension attribute
            const bo = shape.businessObject;
            if (!bo) return;

            // systemCode may be stored as:
            // 1. bo.$attrs['udp:systemCode']  — from our generated XML
            // 2. bo.systemCode                — if bpmn-moddle parsed it as a known attr
            const systemCode =
                (bo.$attrs && bo.$attrs['udp:systemCode']) ||
                bo['udp:systemCode'] ||
                null;

            if (!systemCode) return;

            setLaneCtxMenu({
                x: e.clientX,
                y: e.clientY,
                systemCode,
                laneName: bo.name || bpmnElementId,
            });
        };

        container.addEventListener('contextmenu', handleContextMenu);

        return () => {
             modeler.destroyed = true;
             modeler.off?.('commandStack.changed', handleCommandStackChanged);
             modeler.off?.('import.render.start', handleImportRenderStart);
             modeler.off?.('import.render.complete', handleImportRenderComplete);
             modeler.off?.('import.done', handleImportDone);
             modeler.destroy();
             container.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [diagramId, projectId, importXmlIntoModeler, markDraftChanged]);

    // Load diagram logic
    useEffect(() => {
        if (!diagramId) return;

        const loadGeneration = diagramGenerationRef.current + 1;
        diagramGenerationRef.current = loadGeneration;
        let cancelled = false;
        const isCurrentLoad = () => !cancelled && diagramGenerationRef.current === loadGeneration;

        const load = async () => {
             try {
                 draftRevisionRef.current += 1;
                 draftDirtyRef.current = false;
                 const data = await getDiagramById(projectId, diagramId);
                 if (!isCurrentLoad()) return;
                 const localDraft = readDiagramDraft(buildDiagramDraftKey({
                     projectId,
                     diagramId: data.id,
                     mode: 'bpmn',
                 }));
                 const recoveredDraft = localDraft?.mode === 'bpmn' ? localDraft : null;
                 const restoredMeta = recoveredDraft?.meta || data.meta;
                 if (!isCurrentLoad()) return;
                 setCurrentDiagramId(data.id);
                 setCurrentDiagramMeta(restoredMeta);
                 setDiagramGitlabPath(data.gitlab_path || null);

                 // bpmnXml holds the BPMN XML for the modeler; code/bpmnDsl hold the custom DSL
                 const xml = restoredMeta?.bpmnXml || data.meta?.bpmnXml || DEFAULT_BPMN_XML;

                 try {
                     await importXmlIntoModeler(xml, isCurrentLoad);
                 } catch (err) {
                     if (isCurrentLoad()) {
                         console.error('Error importing BPMN', err);
                     }
                 }
                 if (!isCurrentLoad()) return;

                 // Restore DSL panel if previously saved (guard against old diagrams that stored BPMN XML in code)
                 const savedDsl = recoveredDraft
                     ? (recoveredDraft.meta?.bpmnDsl ?? recoveredDraft.code ?? '')
                     : (data.meta?.bpmnDsl ?? data.meta?.code ?? '');
                 if (savedDsl && !savedDsl.startsWith('<?xml')) {
                     latestDslRef.current = savedDsl;
                     setCodePanelDsl(savedDsl);
                 } else {
                     latestDslRef.current = '';
                     setCodePanelDsl('');
                 }
                 dslSourceRef.current = recoveredDraft?.meta?.bpmnDslSource || (recoveredDraft ? 'user' : 'loaded');
                 if (recoveredDraft && isCurrentLoad()) {
                     markDraftChanged();
                 }
             } catch (e) {
                 if (isCurrentLoad()) {
                     const localDraft = readDiagramDraft(buildDiagramDraftKey({ projectId, diagramId, mode: 'bpmn' }));
                     if (localDraft?.mode === 'bpmn') {
                         const restoredMeta = localDraft.meta || null;
                         const savedDsl = restoredMeta?.bpmnDsl ?? localDraft.code ?? '';
                         setCurrentDiagramId(diagramId);
                         setCurrentDiagramMeta(restoredMeta);
                         setDiagramGitlabPath(null);
                         latestDslRef.current = savedDsl;
                         dslSourceRef.current = restoredMeta?.bpmnDslSource || 'user';
                         setCodePanelDsl(savedDsl);
                         markDraftChanged();
                         await importXmlIntoModeler(restoredMeta?.bpmnXml || DEFAULT_BPMN_XML, isCurrentLoad);
                     } else {
                         console.error('Failed to load diagram', e);
                     }
                 }
             }
        };
        load();
        return () => { cancelled = true; };
    }, [diagramId, projectId, importXmlIntoModeler, markDraftChanged]);

    useEffect(() => {
        const loadGeneration = newDiagramLoadGenerationRef.current + 1;
        newDiagramLoadGenerationRef.current = loadGeneration;
        let cancelled = false;
        const isCurrentNewLoad = () => !cancelled
            && !diagramId
            && newDiagramLoadGenerationRef.current === loadGeneration;

        if (diagramId) return () => { cancelled = true; };

        const localDraft = readDiagramDraft(buildDiagramDraftKey({
            projectId,
            diagramId: null,
            mode: 'bpmn',
        }));
        const recoveredDraft = localDraft?.mode === 'bpmn' ? localDraft : null;
        const restoredMeta = recoveredDraft?.meta || null;
        const savedDsl = restoredMeta?.bpmnDsl ?? recoveredDraft?.code ?? '';
        const xml = restoredMeta?.bpmnXml || DEFAULT_BPMN_XML;

        if (!isCurrentNewLoad()) return () => { cancelled = true; };
        setCurrentDiagramId(null);
        setCurrentDiagramMeta(restoredMeta);
        setDiagramGitlabPath(null);
        latestDslRef.current = savedDsl;
        dslSourceRef.current = restoredMeta?.bpmnDslSource || (recoveredDraft ? 'user' : 'loaded');
        setCodePanelDsl(recoveredDraft ? savedDsl : undefined);
        if (recoveredDraft) {
            markDraftChanged();
        }
        importXmlIntoModeler(xml, isCurrentNewLoad)
            .catch((error) => {
                if (isCurrentNewLoad()) {
                    console.error('Error importing recovered BPMN draft', error);
                }
            });

        return () => { cancelled = true; };
    }, [diagramId, projectId, importXmlIntoModeler, markDraftChanged]);

    const draftKey = buildDiagramDraftKey({ projectId, diagramId: currentDiagramId, mode: 'bpmn' });
    useEffect(() => { draftKeyRef.current = draftKey; }, [draftKey]);

    const resolveDraftDsl = useCallback((xml, fallbackDsl = latestDslRef.current) => {
        if (dslSourceRef.current === 'user') return fallbackDsl ?? '';
        try {
            return xmlToDsl(xml, dslElementMapRef.current) || fallbackDsl || '';
        } catch (error) {
            console.warn('[BPMNDraft] DSL recovery conversion failed:', error);
            return fallbackDsl ?? '';
        }
    }, []);

    const persistLocalDraft = useCallback((dsl = latestDslRef.current) => {
        const xml = latestBpmnXmlRef.current;
        const resolvedDsl = resolveDraftDsl(xml, dsl);
        writeDiagramDraft(draftKeyRef.current, {
            projectId,
            diagramId: currentDiagramId,
            mode: 'bpmn',
            code: resolvedDsl,
            meta: withDraftStatus({
                ...(currentDiagramMeta || {
                    name: 'New BPMN Diagram',
                    domain: 'Business Architecture',
                    version: 1,
                    type: 'bpmn',
                    createdAt: new Date().toISOString(),
                }),
                type: 'bpmn',
                bpmnXml: xml,
                bpmnDsl: resolvedDsl,
                bpmnDslSource: dslSourceRef.current,
                code: resolvedDsl,
                updatedAt: new Date().toISOString(),
            }),
            sync_status: DIAGRAM_DRAFT_STATUS,
            elements: [],
            view: { zoom },
        });
    }, [projectId, currentDiagramId, currentDiagramMeta, zoom, resolveDraftDsl]);

    useEffect(() => {
        persistLocalDraftRef.current = persistLocalDraft;
    }, [persistLocalDraft]);

    const handleSaveDiagram = useCallback(async ({
        silent = false,
        metaOverride = null,
        preserveDirty = false,
        suppressErrorToast = false,
    } = {}) => {
        const saveModeler = modelerRef.current;
        if (!saveModeler) return;
        const saveRevision = draftRevisionRef.current;
        const saveGeneration = diagramGenerationRef.current;
        const saveDiagramId = currentDiagramId;
        const saveDraftKey = draftKeyRef.current;
        const saveMeta = currentDiagramMeta;
        const saveDsl = latestDslRef.current ?? codePanelDsl ?? '';
        const saveDslSource = dslSourceRef.current;
        const isCurrentSave = () => (
            diagramGenerationRef.current === saveGeneration
            && currentDiagramIdRef.current === saveDiagramId
            && draftKeyRef.current === saveDraftKey
            && modelerRef.current === saveModeler
        );

        try {
            const requestedSyncStatus = metaOverride?.sync_status || null;
            const { xml } = await saveModeler.saveXML({ format: true });
            if (!isCurrentSave()) return null;
            latestBpmnXmlRef.current = xml;

            // Auto-sync DSL from current visual state so published content stays up to date.
            const freshDsl = xmlToDsl(xml, dslElementMapRef.current);
            const payloadDsl = freshDsl && saveDslSource !== 'user' ? freshDsl : saveDsl;
            if (freshDsl && saveDslSource !== 'user') {
                latestDslRef.current = freshDsl;
                dslSourceRef.current = 'visual';
                setCodePanelDsl(freshDsl);
            }

            let pngPreview = null;
            try {
                const { svg } = await saveModeler.saveSVG();
                const canvas = document.createElement('canvas');
                canvas.width = 1200;
                canvas.height = 800;
                const ctx = canvas.getContext('2d');
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
                });
                pngPreview = canvas.toDataURL('image/png').split(',')[1];
            } catch (e) {
                console.warn('[BPMN Save] PNG generation failed:', e);
            }

            if (!isCurrentSave()) return null;

            const payload = {
                meta: {
                    ...(saveMeta || {
                        name: 'New BPMN Diagram',
                        domain: 'Business Architecture',
                        version: 1,
                        type: 'bpmn',
                        createdAt: new Date().toISOString()
                    }),
                    ...(metaOverride || {}),
                    type: 'bpmn',
                    updatedAt: new Date().toISOString(),
                    bpmnXml: xml,
                    // Save DSL separately so it survives reload; code field holds DSL text
                    bpmnDsl: payloadDsl,
                    code: payloadDsl,
                    linkedSystems: saveMeta?.linkedSystems || []
                },
                elements: [],
                view: { zoom },
                ...(pngPreview ? { png_preview: pngPreview } : {}),
                ...(requestedSyncStatus ? { sync_status: requestedSyncStatus } : {}),
            };

            if (currentDiagramId) {
                const updated = await updateDiagram(projectId, currentDiagramId, payload);
                if (isCurrentSave()) {
                    setCurrentDiagramMeta(updated.meta);
                    if (!silent) setToast({ type: 'success', title: 'Сохранено' });
                    if (!preserveDirty && draftRevisionRef.current === saveRevision) draftDirtyRef.current = false;
                }
                return updated;
            } else {
                const created = await createDiagram(projectId, payload);
                if (isCurrentSave()) {
                    setCurrentDiagramId(created.id);
                    setCurrentDiagramMeta(created.meta);
                    if (!silent) setToast({ type: 'success', title: 'Диаграмма создана' });
                    if (!preserveDirty && draftRevisionRef.current === saveRevision) draftDirtyRef.current = false;
                }
                return created;
            }
        } catch (e) {
            if (!isCurrentSave()) return null;
            console.error('Save error', e);
            if (!suppressErrorToast) {
                setToast({ type: 'error', title: 'Ошибка сохранения', message: e.message });
            }
            throw e;
        }
    }, [currentDiagramId, currentDiagramMeta, projectId, zoom, codePanelDsl]);

    const persistTemporaryDiagram = useCallback(() => {
        const snapshot = {
            projectId,
            diagramId: currentDiagramId,
            meta: currentDiagramMeta,
            xml: latestBpmnXmlRef.current,
            dsl: resolveDraftDsl(latestBpmnXmlRef.current, latestDslRef.current ?? codePanelDsl ?? ''),
            zoom,
        };
        const payload = {
            meta: withDraftStatus({
                ...(snapshot.meta || { name: 'New BPMN Diagram', domain: 'Business Architecture', version: 1, type: 'bpmn' }),
                type: 'bpmn',
                bpmnXml: snapshot.xml,
                bpmnDsl: snapshot.dsl,
                code: snapshot.dsl,
                updatedAt: new Date().toISOString(),
            }),
            elements: [],
            view: { zoom: snapshot.zoom },
            sync_status: DIAGRAM_DRAFT_STATUS,
        };
        const request = snapshot.diagramId
            ? updateDiagram(snapshot.projectId, snapshot.diagramId, payload)
            : createDiagram(snapshot.projectId, payload);
        return Promise.resolve(request).catch((error) => {
            console.warn('[BPMNDraft] backend draft save failed:', error);
        });
    }, [projectId, currentDiagramId, currentDiagramMeta, codePanelDsl, zoom, resolveDraftDsl]);

    const handlePublishDiagram = useCallback(async (selectedEntry) => {
        if (!currentDiagramId || isPublishing) return;
        setIsPublishing(true);
        try {
            const options = {};
            if (selectedEntry?.code) {
                options.rootEntryCode = selectedEntry.code;
                options.rootRegistrySlug = selectedEntry.registry_type?.slug || selectedEntry.registryType?.slug || '';
            }

            // Generate PNG from BPMN SVG
            if (modelerRef.current) {
                try {
                    const { svg } = await modelerRef.current.saveSVG();
                    const canvas = document.createElement('canvas');
                    canvas.width = 1200;
                    canvas.height = 800;
                    const ctx = canvas.getContext('2d');
                    const img = new Image();
                    await new Promise((resolve) => {
                        img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
                    });
                    options.pngPreview = canvas.toDataURL('image/png').split(',')[1];
                } catch (e) {
                    console.warn('[BPMN Publish] PNG generation failed:', e);
                }
            }

            await publishDiagram(projectId, currentDiagramId, options);
            setToast({ type: 'success', title: 'Опубликовано в GitLab' });
        } catch (e) {
            console.error('Publish error', e);
            // Error message format from backend: "API Error (500): sync conflict: ..."
            if (e.message && e.message.includes('sync conflict')) {
                setShowConflict(true);
            } else {
                setToast({ type: 'error', title: 'Ошибка публикации', message: e.message });
            }
        } finally {
            setIsPublishing(false);
        }
    }, [currentDiagramId, isPublishing, projectId]);

    const openSaveDiagramModal = useCallback(() => {
        setShowDiagramModal(true);
    }, []);

    const handleModalSave = useCallback(async ({ name, domain }) => {
        const saveRevision = draftRevisionRef.current;
        const saveGeneration = diagramGenerationRef.current;
        const saveDiagramId = currentDiagramId;
        const saveDraftKey = draftKeyRef.current;
        const saved = await handleSaveDiagram({ silent: true, metaOverride: { name, domain } });
        modalSaveContextRef.current = {
            result: saved,
            generation: saveGeneration,
            diagramId: saveDiagramId,
            draftKey: saveDraftKey,
        };
        if (draftRevisionRef.current === saveRevision) {
            clearDiagramDraft(draftKeyRef.current);
            draftDirtyRef.current = false;
        }
        return saved;
    }, [handleSaveDiagram]);

    const handleModalSaved = useCallback((saved) => {
        const saveContext = modalSaveContextRef.current;
        const isCreatedDiagram = saveContext?.diagramId == null && currentDiagramIdRef.current === saved?.id;
        const isCurrentModalSave = saved
            && saveContext?.result === saved
            && saveContext.generation === diagramGenerationRef.current
            && (saveContext.diagramId === currentDiagramIdRef.current || isCreatedDiagram)
            && (saveContext.draftKey === draftKeyRef.current || isCreatedDiagram);
        if (!isCurrentModalSave) return;
        setCurrentDiagramId(saved.id);
        setCurrentDiagramMeta(saved.meta || null);
        setDiagramGitlabPath(saved.gitlab_path || diagramGitlabPath || null);
        setToast({ type: 'success', title: currentDiagramId ? 'Сохранено' : 'Диаграмма создана' });
    }, [currentDiagramId, diagramGitlabPath]);

    const handleCodePanelDslChange = useCallback((dsl) => {
        latestDslRef.current = dsl;
        dslSourceRef.current = 'user';
        markDraftChanged();
        persistLocalDraft(dsl);
    }, [markDraftChanged, persistLocalDraft]);

    const confirmCanvasExit = useCallback(() => {
        if (!draftDirtyRef.current) return true;
        if (!window.confirm(DIAGRAM_EXIT_CONFIRM_MESSAGE)) return false;
        persistLocalDraft();
        persistTemporaryDiagram();
        return true;
    }, [persistLocalDraft, persistTemporaryDiagram]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!draftDirtyRef.current) return undefined;
            // Unload work is best-effort; local storage contains the latest async XML snapshot.
            persistLocalDraft();
            persistTemporaryDiagram();
            event.preventDefault();
            event.returnValue = DIAGRAM_EXIT_CONFIRM_MESSAGE;
            return DIAGRAM_EXIT_CONFIRM_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [persistLocalDraft, persistTemporaryDiagram]);

    const openPublishModalWithLintPreflight = useCallback(async () => {
        const saveRevision = draftRevisionRef.current;
        await handleSaveDiagram({ silent: true });
        if (draftRevisionRef.current === saveRevision) {
            clearDiagramDraft(draftKeyRef.current);
            draftDirtyRef.current = false;
        }
        const currentDsl = latestDslRef.current ?? codePanelDsl ?? '';
        try {
            setPublishLintDiagnostics(collectPublishLintDiagnostics({ mode: 'bpmn', code: currentDsl }));
        } catch (error) {
            console.warn('[PublishLint] BPMN diagnostics failed:', error);
            setPublishLintDiagnostics([]);
        }
        setShowPublishModal(true);
    }, [handleSaveDiagram, codePanelDsl]);

    // Re-publish helper: calls publishDiagram directly, bypassing the isPublishing guard
    // in handlePublishDiagram to avoid stale-closure issues from conflict resolution flow.
    const _republishDirect = useCallback(async () => {
        let pngPreview;
        if (modelerRef.current) {
            try {
                const { svg } = await modelerRef.current.saveSVG();
                const canvas = document.createElement('canvas');
                canvas.width = 1200; canvas.height = 800;
                const ctx = canvas.getContext('2d');
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
                });
                pngPreview = canvas.toDataURL('image/png').split(',')[1];
            } catch (_) {}
        }
        await publishDiagram(projectId, currentDiagramId, pngPreview ? { pngPreview } : {});
    }, [currentDiagramId, projectId]);

    const handleConflictAcceptPlatform = useCallback(async () => {
        if (!currentDiagramId || isResolvingConflict) return;
        setIsResolvingConflict(true);
        setIsPublishing(true);
        try {
            await resolveConflictAcceptPlatform(currentDiagramId);
            setShowConflict(false);
            await _republishDirect();
            setToast({ type: 'success', title: 'Опубликовано в GitLab' });
        } catch (e) {
            console.error('[ConflictResolution] accept-platform error:', e);
            setToast({ type: 'error', title: 'Ошибка разрешения конфликта', message: e.message });
        } finally {
            setIsResolvingConflict(false);
            setIsPublishing(false);
        }
    }, [currentDiagramId, isResolvingConflict, _republishDirect]);

    const handleConflictAcceptGitlab = useCallback(async () => {
        if (!currentDiagramId || isResolvingConflict) return;
        setIsResolvingConflict(true);
        try {
            await resolveConflictAcceptGitlab(currentDiagramId);
            setShowConflict(false);
            // Reload diagram — backend now has GitLab DSL in meta.bpmnDsl
            const data = await getDiagramById(projectId, currentDiagramId);
            setCurrentDiagramMeta(data.meta);
            const savedDsl = data.meta?.bpmnDsl || data.meta?.code || '';
            if (savedDsl && !savedDsl.startsWith('<?xml')) {
                latestDslRef.current = savedDsl;
                dslSourceRef.current = 'loaded';
                setCodePanelDsl(savedDsl);
                setShowCodePanel(true); // open code panel to show the GitLab DSL
            }
            // NOTE: bpmnXml is NOT updated — user must click "Применить" in the code panel
            // to apply the GitLab DSL to the visual modeler.
            setToast({
                type: 'success',
                title: 'Версия GitLab загружена в панель кода',
                message: 'Нажмите «Применить» в панели кода для обновления диаграммы',
            });
        } catch (e) {
            console.error('[ConflictResolution] accept-gitlab error:', e);
            setToast({ type: 'error', title: 'Ошибка загрузки версии GitLab', message: e.message });
        } finally {
            setIsResolvingConflict(false);
        }
    }, [currentDiagramId, isResolvingConflict, projectId]);

    useKeyboardShortcuts({
        onSaveDiagram: openSaveDiagramModal
    });

    const handleExportImage = useCallback(async (format = 'svg') => {
        if (!modelerRef.current) return;
        try {
            const { svg } = await modelerRef.current.saveSVG();

            if (format === 'svg') {
                const blob = new Blob([svg], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = (currentDiagramMeta?.name || 'diagram') + '.svg';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svg, "image/svg+xml");
                const svgEl = svgDoc.documentElement;
                const width = parseFloat(svgEl.getAttribute('width'));
                const height = parseFloat(svgEl.getAttribute('height'));

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(svgBlob);

                img.onload = () => {
                    canvas.width = width;
                    canvas.height = height;

                    if (format === 'jpg') {
                        ctx.fillStyle = theme.canvasBackground || '#ffffff';
                        ctx.fillRect(0, 0, width, height);
                    }

                    ctx.drawImage(img, 0, 0);

                    const dataUrl = canvas.toDataURL(format === 'jpg' ? 'image/jpeg' : 'image/png');
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = (currentDiagramMeta?.name || 'diagram') + '.' + format;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                };
                img.src = url;
            }
        } catch (err) {
            console.error('Export error', err);
        }
    }, [currentDiagramMeta, theme.canvasBackground]);

    const onLoadDiagram = async (diagram, id) => {
        diagramGenerationRef.current += 1;
        newDiagramLoadGenerationRef.current += 1;
        const loadGeneration = diagramGenerationRef.current;
        draftRevisionRef.current += 1;
        draftDirtyRef.current = false;
        setCurrentDiagramId(id);
        setCurrentDiagramMeta(diagram.meta);
        setDiagramGitlabPath(diagram.gitlab_path || null);
        const xml = diagram.meta?.bpmnXml || DEFAULT_BPMN_XML;
        imperativeLoadRef.current = { generation: loadGeneration, xml };
        const savedDsl = diagram.meta?.bpmnDsl ?? diagram.meta?.code ?? '';
        if (savedDsl && !savedDsl.startsWith('<?xml')) {
            latestDslRef.current = savedDsl;
            setCodePanelDsl(savedDsl);
        } else {
            latestDslRef.current = '';
            setCodePanelDsl('');
        }
        dslSourceRef.current = 'loaded';
        const isCurrentImperativeLoad = () => (
            diagramGenerationRef.current === loadGeneration
            && imperativeLoadRef.current?.generation === loadGeneration
        );
        try {
            await importXmlIntoModeler(xml, isCurrentImperativeLoad);
        } catch (err) {
            if (isCurrentImperativeLoad()) {
                console.error('Error importing BPMN', err);
            }
        }
    };

    const handleZoomIn = () => {
        modelerRef.current?.get('zoomScroll').stepZoom(1);
    };

    const handleZoomOut = () => {
        modelerRef.current?.get('zoomScroll').stepZoom(-1);
    };

    const handleFitToBoard = () => {
        modelerRef.current?.get('canvas').zoom('fit-viewport');
    };

    const handleSyncToCode = useCallback(async () => {
        if (!modelerRef.current) return;
        try {
            const { xml } = await modelerRef.current.saveXML({ format: true });
            const dsl = xmlToDsl(xml, dslElementMapRef.current);
            if (!dsl) return;
            setCodePanelDsl(dsl);
            if (!showCodePanel) setShowCodePanel(true);
        } catch (err) {
            console.error('Sync to code error:', err);
        }
    }, [showCodePanel]);

    const handleGoToSystem = useCallback(() => {
        if (!laneCtxMenu?.systemCode) return;
        const pid = projectId || currentDiagramMeta?.projectId || 'local';
        const url = `/projects/${encodeURIComponent(pid)}/systems?selected=${encodeURIComponent(laneCtxMenu.systemCode)}`;
        window.open(url, '_blank');
        setLaneCtxMenu(null);
    }, [laneCtxMenu, projectId, currentDiagramMeta]);

    return (
        <div
            style={{ width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={() => laneCtxMenu && setLaneCtxMenu(null)}
            onKeyDown={(e) => e.key === 'Escape' && setLaneCtxMenu(null)}
        >
            <ToolbarBpmn
                zoomIn={handleZoomIn}
                zoomOut={handleZoomOut}
                fitToBoard={handleFitToBoard}
                onSaveDiagram={openSaveDiagramModal}
                onLoadDiagram={onLoadDiagram}
                currentDiagramId={currentDiagramId}
                currentDiagramMeta={currentDiagramMeta}
                zoom={zoom}
                topOffset={0}
                onExportImage={handleExportImage}
                onUndo={() => console.log('Undo not implemented yet')}
                onRedo={() => console.log('Redo not implemented yet')}
                canUndo={false}
                canRedo={false}
                showCodePanel={showCodePanel}
                onToggleCodePanel={() => setShowCodePanel((v) => !v)}
                onSyncToCode={handleSyncToCode}
                onPublishDiagram={currentDiagramId ? openPublishModalWithLintPreflight : undefined}
                isPublishing={isPublishing}
                diagramGitlabPath={diagramGitlabPath}
                onShowHistory={diagramGitlabPath ? () => setShowHistoryModal(true) : undefined}
                onBeforeNavigate={confirmCanvasExit}
            />

            {showDiagramModal && (
                <DiagramModal
                    visible={showDiagramModal}
                    onClose={() => setShowDiagramModal(false)}
                    elements={[]}
                    onLoadDiagram={onLoadDiagram}
                    currentDiagramId={currentDiagramId}
                    zoom={zoom}
                    scrollPosition={{ left: 0, top: 0 }}
                    currentDiagramMeta={currentDiagramMeta}
                    initialTab="save"
                    code={latestDslRef.current ?? codePanelDsl ?? ''}
                    setCode={(next) => {
                        latestDslRef.current = next;
                        dslSourceRef.current = 'user';
                        setCodePanelDsl(next);
                    }}
                    defaultDiagramType="bpmn"
                    fixedDiagramType="bpmn"
                    onSave={handleModalSave}
                    onSaved={handleModalSaved}
                    projectId={projectId}
                />
            )}

            {showHistoryModal && currentDiagramId && (
                <DiagramHistoryModal
                    projectId={projectId}
                    diagramId={currentDiagramId}
                    diagramName={currentDiagramMeta?.name || 'BPMN диаграмма'}
                    onClose={() => setShowHistoryModal(false)}
                />
            )}

            <DiagramPublishModal
                isOpen={showPublishModal}
                onClose={() => {
                    setPublishLintDiagnostics([]);
                    setShowPublishModal(false);
                }}
                diagramId={currentDiagramId}
                projectId={projectId}
                isPublishing={isPublishing}
                lintDiagnostics={publishLintDiagnostics}
                onConfirm={(selectedEntry) => {
                    setPublishLintDiagnostics([]);
                    setShowPublishModal(false);
                    handlePublishDiagram(selectedEntry);
                }}
            />

            <DiagramToastModal
                isOpen={!!toast}
                type={toast?.type}
                title={toast?.title || ''}
                message={toast?.message || ''}
                onClose={() => setToast(null)}
            />

            <DiagramConflictModal
                isOpen={showConflict}
                onClose={() => setShowConflict(false)}
                onAcceptPlatform={handleConflictAcceptPlatform}
                onAcceptGitlab={handleConflictAcceptGitlab}
                isResolving={isResolvingConflict}
            />

            <div
                ref={containerRef}
                style={{
                    flex: 1,
                    backgroundColor: theme.canvasBackground || '#fff',
                    position: 'relative',
                    marginTop: 56,
                    marginRight: showCodePanel ? CODE_PANEL_WIDTH : 0,
                    transition: 'margin-right 0.2s ease',
                }}
            />

            {showCodePanel && (
                <BpmnCodePanel
                    onClose={() => setShowCodePanel(false)}
                    dslText={codePanelDsl}
                    onElementMapChange={(map) => { dslElementMapRef.current = map; }}
                    onDslChange={handleCodePanelDslChange}
                    onGenerateBpmn={(xml, elementMap) => {
                        if (elementMap) dslElementMapRef.current = elementMap;
                        importXmlIntoModeler(xml).catch((err) =>
                            console.error('Error importing generated BPMN', err)
                        );
                    }}
                />
            )}

            {/* ── Lane context menu ── */}
            {laneCtxMenu && (
                <LaneContextMenu
                    x={laneCtxMenu.x}
                    y={laneCtxMenu.y}
                    laneName={laneCtxMenu.laneName}
                    systemCode={laneCtxMenu.systemCode}
                    theme={theme}
                    onGoToSystem={handleGoToSystem}
                    onClose={() => setLaneCtxMenu(null)}
                />
            )}
        </div>
    );
};

// ─── Lane Context Menu component ─────────────────────────────────────────────

const LaneContextMenu = ({ x, y, laneName, systemCode, theme, onGoToSystem, onClose }) => {
    const bg = theme.contextMenu?.background || theme.toolbar?.background || '#fff';
    const textColor = theme.contextMenu?.text || theme.toolbar?.text || '#1e293b';
    const border = theme.contextMenu?.border || theme.toolbar?.border || '#e2e8f0';
    const shadow = theme.contextMenu?.shadow || '0 4px 16px rgba(0,0,0,0.15)';

    const itemStyle = {
        padding: '7px 14px',
        cursor: 'pointer',
        borderRadius: 4,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        whiteSpace: 'nowrap',
        color: textColor,
        transition: 'background 0.15s',
    };

    return (
        <div
            style={{
                position: 'fixed',
                left: x,
                top: y,
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 8,
                padding: '4px 0',
                zIndex: 9999,
                minWidth: 210,
                boxShadow: shadow,
            }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Header */}
            <div style={{
                padding: '5px 14px 6px',
                borderBottom: `1px solid ${border}`,
                marginBottom: 3,
            }}>
                <div style={{ fontSize: 11, opacity: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: textColor }}>
                    Дорожка
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: textColor, marginTop: 1 }}>
                    {laneName}
                </div>
                <div style={{ fontSize: 11, color: '#3b82f6', fontFamily: 'monospace', marginTop: 1 }}>
                    {`<<${systemCode}>>`}
                </div>
            </div>

            {/* Go to system */}
            <div
                style={itemStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.currentTheme === 'dark' ? '#374151' : '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={onGoToSystem}
            >
                <span style={{ fontSize: 15 }}>🔗</span>
                <span>Перейти к системе в реестре ИС</span>
            </div>

            {/* Divider + Close */}
            <div style={{ height: 1, background: border, margin: '3px 0' }} />
            <div
                style={{ ...itemStyle, opacity: 0.6 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.currentTheme === 'dark' ? '#374151' : '#f1f5f9'; e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.6'; }}
                onClick={onClose}
            >
                <span style={{ fontSize: 14 }}>✕</span>
                <span>Закрыть</span>
            </div>
        </div>
    );
};

export default BoardBpmn;
